import * as THREE from 'three';
import { CSM } from 'three/examples/jsm/csm/CSM.js';
import { createBestRenderer, type RendererCapabilities } from './webgpu-renderer';
import { loadGraphicalMap, disposeMapAssetState } from '../map/map-asset-loader';
import { getActiveMapDefinition } from '../map/map-registry';
import { disposeLoaderCache } from '../map/map-loader';
import type { PropDef } from '../map/map-format';
import { disposeLightingZones } from '../map/lighting-zones';
import { initPVS, batchZoneGeometry, buildCollisionBVH, getCollisionBVH, getVisibilityZonesForMap, disposeOcclusion } from '../map/occlusion';
import { setCollisionBVH } from '../gameplay/collision';
import { createInteractiveElementsForMap, type InteractiveElementManager } from '../map/interactive-elements';
import { loadHDREnvMap, disposeEnvMapTargets } from './scene-envmap';
import { disposeBlobShadows } from './contact-shadow';
import { textureCache } from './scene-textures';
import { mat, materialCache, clearMaterialCaches, upgradeMaterials } from './scene-materials';
import type { MatKey } from './scene-materials';
import { PBRTextureLoader } from './texture-loader';
import { LightmapSystem } from './lightmap-system';
import { settingsStore } from '../core/settings';
import type { GraphicsQuality } from '../core/settings';
import { isMobile } from '../core/device';
import { getTextureStreamer, disposeTextureStreamer, type TextureStreamer } from './texture-streamer';
import { disposeTrimSheets } from '../map/trim-sheets';
import { initDecals, initPermanentDecals, disposePermanentDecals } from '../map/decals';
import { loadProps, batchAllProps, disposeProps, onBlockingPropsLoaded, onPropGLBSwapped } from '../map/props';
import { VegetationSystem } from '../map/vegetation';

// ---------------------------------------------------------------------------
// Scene setup: de_outpost_v6 (GLB) default with PVS occlusion + atmosphere.
// Map asset loaded via map-asset-loader; KTX2 PBR textures + baked lightmaps
// stream in via texture-streamer + LightmapSystem.
// ---------------------------------------------------------------------------

let renderer: THREE.WebGLRenderer;
let rendererCapabilities: RendererCapabilities | null = null;
let scene: THREE.Scene;
let pbrTextureLoader: PBRTextureLoader | null = null;
let lightmapSystem: LightmapSystem | null = null;
let textureStreamer: TextureStreamer | null = null;

// Guard against async lightmap application on disposed scenes
let sceneDisposed = false;

let vegetation: VegetationSystem | null = null;

// Cascaded Shadow Maps state
let csm: CSM | null = null;
let fallbackSun: THREE.DirectionalLight | null = null;

/** Arena half-size in meters. Exported so other systems can reference it. */
export const ARENA_HALF = 80;

// ═══════════════════════════════════════════════════════════════════════════
//  Scene creation
// ═══════════════════════════════════════════════════════════════════════════

/** Material factory adapter — exposes scene.ts's mat() to the map loader. */
const materialFactory = {
  get(key: string, repeatX = 1, repeatY = 1): THREE.MeshStandardMaterial {
    return mat(key as MatKey, repeatX, repeatY, renderer);
  },
};

function sceneHasLights(targetScene: THREE.Scene): boolean {
  let found = false;
  targetScene.traverse((obj) => {
    if (obj instanceof THREE.Light) found = true;
  });
  return found;
}

function addDefaultMapLighting(targetScene: THREE.Scene): void {
  const quality = settingsStore.get().graphicsQuality;
  const mapId = targetScene.userData.activeMapId as string;

  // Per-map ambient lighting colors
  let ambientColor = 0xf4d7aa; let ambientInt = 0.55;
  let hemiSky = 0xf7ead2; let hemiGround = 0x6e604c; let hemiInt = 0.4;
  let sunColor = 0xfff2d0; let sunInt = 1.15;
  let sunPos: [number, number, number] = [55, 85, 35];

  if (mapId === 'de_outpost_v6') {
    ambientColor = 0xb0bcc8; ambientInt = 0.5;
    hemiSky = 0xc8d8e8; hemiGround = 0x8a7a60; hemiInt = 0.4;
    sunColor = 0xfff0d0; sunInt = 1.2;
    sunPos = [25, 50, 15];
  }

  const ambient = new THREE.AmbientLight(ambientColor, ambientInt);
  const hemi = new THREE.HemisphereLight(hemiSky, hemiGround, hemiInt);
  targetScene.add(ambient, hemi);

  // mobile-low: shadows disabled entirely — boost ambient to compensate
  if (quality === 'mobile-low') {
    ambient.intensity = 0.75;
    hemi.intensity = 0.6;
    const sun = new THREE.DirectionalLight(sunColor, sunInt);
    sun.position.set(...sunPos);
    sun.castShadow = false;
    targetScene.add(sun);
    return;
  }

  // Temporary single DirectionalLight — replaced by CSM when initCSM() is called
  // with the camera reference. Keeps shadows working during initial frames.
  fallbackSun = new THREE.DirectionalLight(sunColor, sunInt);
  fallbackSun.position.set(...sunPos);
  fallbackSun.castShadow = true;

  // mobile-medium: single 512x512 shadow map (no CSM)
  if (quality === 'mobile-medium') {
    fallbackSun.shadow.mapSize.set(512, 512);
  } else {
    fallbackSun.shadow.mapSize.set(2048, 2048);
  }
  fallbackSun.shadow.camera.near = 10;
  fallbackSun.shadow.camera.far = 200;
  fallbackSun.shadow.camera.left = -90;
  fallbackSun.shadow.camera.right = 90;
  fallbackSun.shadow.camera.top = 80;
  fallbackSun.shadow.camera.bottom = -80;
  fallbackSun.shadow.bias = -0.0002;
  fallbackSun.shadow.normalBias = 0.03;
  targetScene.add(fallbackSun);
}

/**
 * Per-zone lightmap configuration for de_outpost_v6.
 *
 * Each entry maps a zone (lightmap filename, sans extension) to the list of
 * mesh-name prefixes that belong to that zone. Mesh-prefix matching is the
 * fallback when `userData.cp_zone` is absent (kit_v6 props don't all carry
 * the custom property). Prefixes are tested in order; the first matching
 * prefix wins, so longer/more-specific prefixes must appear before shorter
 * ones in cross-zone ambiguity cases.
 *
 * Source of truth: the per-zone Blender modules under
 * `scripts/blender/outpost_v6_<zone>.py` — each builder names its meshes
 * `outpost_v6_<ZONE_NAME>_*` and a small number of zones additionally use
 * short hero aliases (e.g. `outpost_v6_a_*` inside a_site_command_center,
 * `outpost_v6_ct_hq_*` inside ct_spawn_hq_compound).
 */
export const ZONE_LIGHTMAPS_V6: ReadonlyArray<{ zone: string; namePrefixes: readonly string[] }> = [
  { zone: 'a_site_command_center', namePrefixes: ['outpost_v6_a_site_command_center_', 'outpost_v6_a_'] },
  { zone: 'b_site_warehouse',      namePrefixes: ['outpost_v6_b_site_warehouse_'] },
  { zone: 'b_tunnels',             namePrefixes: ['outpost_v6_b_tunnels_'] },
  { zone: 'ct_a_tunnel',           namePrefixes: ['outpost_v6_ct_a_tunnel_'] },
  { zone: 'ct_spawn_hq_compound',  namePrefixes: ['outpost_v6_ct_spawn_hq_compound_', 'outpost_v6_ct_hq_'] },
  { zone: 'drainage_channel',      namePrefixes: ['outpost_v6_drainage_channel_'] },
  { zone: 'long_a',                namePrefixes: ['outpost_v6_long_a_'] },
  { zone: 'mid_courtyard',         namePrefixes: ['outpost_v6_mid_courtyard_'] },
  { zone: 'short_a',               namePrefixes: ['outpost_v6_short_a_'] },
  { zone: 't_spawn',               namePrefixes: ['outpost_v6_t_spawn_'] },
  { zone: 'world',                 namePrefixes: ['outpost_v6_world_'] },
];

/**
 * Build a mesh-filter predicate for a single zone. Matches when:
 *   1. `mesh.userData.cp_zone` equals the zone name (Blender-tagged meshes), or
 *   2. `mesh.name` starts with any of the zone's known prefixes.
 *
 * The predicate also rejects meshes already claimed by a previous zone in the
 * current applyZoneLightmaps pass via the shared `claimed` Set, so each mesh
 * receives at most one zone lightmap (whichever zone is enumerated first).
 */
function makeZoneFilter(
  zone: string,
  namePrefixes: readonly string[],
  claimed: Set<THREE.Mesh>,
): (mesh: THREE.Mesh) => boolean {
  return (mesh: THREE.Mesh): boolean => {
    if (claimed.has(mesh)) return false;
    const cpZone = mesh.userData?.cp_zone;
    if (typeof cpZone === 'string' && cpZone === zone) {
      claimed.add(mesh);
      return true;
    }
    const name = mesh.name ?? '';
    for (const prefix of namePrefixes) {
      if (name.startsWith(prefix)) {
        claimed.add(mesh);
        return true;
      }
    }
    return false;
  };
}

/**
 * Load and apply per-zone KTX2 lightmaps for de_outpost_v6.
 *
 * Each zone's lightmap is loaded from `${mapDir}lightmaps/${zone}.ktx2` (or
 * the mobile variant under `lightmaps/mobile/` when running on a touch
 * device). The texture is applied to meshes whose `userData.cp_zone` matches
 * the zone or whose name begins with a known zone prefix.
 *
 * Returns the number of zones whose lightmap was found and applied. Zones
 * with missing files are silently skipped so a partial bake still applies.
 */
export async function applyZoneLightmaps(
  system: LightmapSystem,
  root: THREE.Object3D,
  mapDir: string,
): Promise<number> {
  const loader = system.getLoader();
  const mobile = isMobile();
  const subdir = mobile ? 'lightmaps/mobile/' : 'lightmaps/';
  const claimed = new Set<THREE.Mesh>();
  let appliedZones = 0;

  for (const { zone, namePrefixes } of ZONE_LIGHTMAPS_V6) {
    if (sceneDisposed || !root.parent) return appliedZones;

    const path = `${mapDir}${subdir}${zone}.ktx2`;
    if (!(await loader.exists(path))) continue;

    if (sceneDisposed || !root.parent) return appliedZones;

    await system.applyToScene(root, [
      { path, intensity: 1.0, meshFilter: makeZoneFilter(zone, namePrefixes, claimed) },
    ]);

    if (sceneDisposed) return appliedZones;
    appliedZones++;
  }

  return appliedZones;
}

/**
 * Try to load external lightmap files from the map directory.
 *
 * Attempts the per-zone v6 layout first (`lightmaps/<zone>.ktx2`) so
 * de_outpost_v6's 11 baked zone lightmaps are picked up. Falls back to the
 * legacy monolithic candidates (`lightmap.{ktx2,png,jpg}`) for any map that
 * ships a single combined lightmap.
 */
function applyExternalLightmaps(system: LightmapSystem, root: THREE.Object3D, mapDir: string): void {
  const loader = system.getLoader();
  (async () => {
    // R-H-001: Try the per-zone v6 layout first. de_outpost_v6 ships 11
    // baked KTX2 zone lightmaps; if any of them apply we're done. Legacy
    // monolithic-lightmap maps fall through to the candidate list below.
    const zoneApplied = await applyZoneLightmaps(system, root, mapDir);
    if (sceneDisposed) return;
    if (zoneApplied > 0) return;

    const candidates = [mapDir + 'lightmap.ktx2', mapDir + 'lightmap.png', mapDir + 'lightmap.jpg'];
    for (const path of candidates) {
      if (sceneDisposed) return;
      if (await loader.exists(path)) {
        // M12: After async await, verify scene and root are still valid
        if (sceneDisposed || !root.parent) return;
        await system.applyToScene(root, [{ path, intensity: 1.0 }]);
        // BUG-08: Re-check after applyToScene await — scene may have been disposed during texture load
        if (sceneDisposed) return;
        return;
      }
    }
  })().catch((err) => console.warn('External lightmap loading failed:', err));
}

/** Initialise the Three.js scene and renderer. Returns {scene, renderer}. */
export async function createScene(): Promise<{ scene: THREE.Scene; renderer: THREE.WebGLRenderer }> {
  sceneDisposed = false;

  // H11: Clear material caches at scene creation to prevent stale material retention
  clearMaterialCaches();

  const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
  if (!canvas) throw new Error('#game-canvas not found');

  // Phase 12: WebGPU probe + WebGL2 fallback
  const bestRenderer = await createBestRenderer(canvas, {
    antialias: true,
    powerPreference: 'high-performance',
    preferWebGPU: false,
  });
  renderer = bestRenderer.renderer;
  rendererCapabilities = bestRenderer.capabilities;

  // Apply DPR cap based on quality preset (mobile presets are more aggressive)
  const initialQualityForDPR = settingsStore.get().graphicsQuality;
  switch (initialQualityForDPR) {
    case 'mobile-low':
      renderer.setPixelRatio(1.0);
      break;
    case 'mobile-medium':
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
      break;
    case 'mobile-high':
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2.0));
      break;
    default:
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      break;
  }
  renderer.setSize(window.innerWidth, window.innerHeight);

  scene = new THREE.Scene();

  const activeMap = getActiveMapDefinition();

  // Per-map atmosphere — fog, background, and environment intensity
  // This is the primary visual differentiator between maps.
  const mapAtmosphere: Record<string, { bg: number; fog: number; fogDensity: number; envIntensity: number }> = {
    de_outpost_v6: { bg: 0x8a9aaa, fog: 0x7a8a98, fogDensity: 0.008, envIntensity: 0.6 },   // overcast military (v6 rebuild)
  };
  const atmo = mapAtmosphere[activeMap.id] ?? mapAtmosphere.de_outpost_v6;
  scene.background = new THREE.Color(atmo.bg);
  scene.fog = new THREE.FogExp2(atmo.fog, atmo.fogDensity);

  // Phase QA streaming-load: kick off the HDR env map + GLB fetch IN PARALLEL.
  // The HDR is ~1-2 MiB and decodes in ~150-400 ms; the GLB is 8-50 MiB and
  // takes 1-3 s. Previously these were serialised (`await` then `await`), so
  // the user stared at a black screen until both finished. With Promise.all
  // the wall clock collapses to max(env, glb) instead of env+glb — a 25-40%
  // win on first paint.
  scene.userData.activeMapId = activeMap.id;
  scene.userData.activeMapKind = activeMap.kind;

  // Phase 9: Initialise lightmap system early so its Worker pool can warm
  // up while the env map + GLB are still in flight.
  const initialQuality = settingsStore.get().graphicsQuality;
  lightmapSystem = new LightmapSystem(renderer, initialQuality);

  const [envMap, graphicalMap] = await Promise.all([
    loadHDREnvMap(renderer),
    loadGraphicalMap(activeMap.scenePath),
  ]);

  // L07: If scene was disposed during the parallel async loads, dispose the
  // texture (and let the GLB GC) and bail.
  if (sceneDisposed) {
    envMap?.dispose();
    return { scene, renderer };
  }
  scene.environment = envMap;
  scene.environmentIntensity = atmo.envIntensity;
  scene.add(graphicalMap.root);
  scene.userData.graphicalMap = graphicalMap.data;
  if (!sceneHasLights(scene)) {
    addDefaultMapLighting(scene);
  }

  // Baked lighting — auto-detect embedded lightmaps + check external files
  lightmapSystem!.applyFromGLTF(graphicalMap.root);
  const mapDir = activeMap.scenePath.substring(0, activeMap.scenePath.lastIndexOf('/') + 1);
  applyExternalLightmaps(lightmapSystem!, graphicalMap.root, mapDir);

  // R-C-002: Wire the decal system to this scene. Previously this was only
  // called from `map-loader.ts` (legacy JSON path), so on the GLB map path
  // (de_outpost_v6) `decalScene` stayed null and every runtime `spawnDecal`
  // (bullet impacts, blood, scorch) silently early-returned. Also seed the
  // map-baked permanent decals — `initPermanentDecals` is idempotent so a
  // map switch or replay reload won't duplicate them.
  initDecals(scene);
  initPermanentDecals(scene);

  // Phase 4 (Blender pipeline): When baked lightmaps are present, reduce
  // runtime directional light intensity to avoid double-lit surfaces (baked
  // GI already contains direct + indirect diffuse contribution).
  let hasLightmaps = false;
  graphicalMap.root.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      const meshMat = (child as THREE.Mesh).material as THREE.MeshStandardMaterial;
      if (meshMat?.lightMap) {
        hasLightmaps = true;
      }
    }
  });
  if (hasLightmaps) {
    scene.traverse((child) => {
      if ((child as THREE.DirectionalLight).isDirectionalLight) {
        (child as THREE.DirectionalLight).intensity *= 0.7;
      }
    });
  }

  // Phase 4 (Blender pipeline): Apply max anisotropic filtering to all PBR
  // textures loaded from glTF. The GLTFLoader does not set anisotropy, so
  // Blender-exported textures need this post-load pass for sharp detail at
  // oblique viewing angles.
  const maxAniso = renderer.capabilities.getMaxAnisotropy();
  graphicalMap.root.traverse((child) => {
    if (!(child as THREE.Mesh).isMesh) return;
    const meshMat = (child as THREE.Mesh).material as THREE.MeshStandardMaterial;
    if (!meshMat) return;
    const texKeys = ['map', 'normalMap', 'roughnessMap', 'aoMap', 'lightMap', 'metalnessMap', 'emissiveMap'] as const;
    for (const key of texKeys) {
      const tex = (meshMat as any)[key] as THREE.Texture | null;
      if (tex && tex.anisotropy < maxAniso) {
        tex.anisotropy = maxAniso;
      }
    }
  });

  // Spawn GLB props from glTF cp_kind=prop markers embedded in the map GLB.
  const propMarkers = graphicalMap.data.props ?? [];
  const propDefs: PropDef[] = propMarkers.map((marker) => ({
    type: String(marker.data.cp_prop_type || marker.name.replace('prop_', '')),
    position: marker.position,
    rotation: marker.rotation,
    scale: marker.scale,
    collide: marker.data.cp_collide === true || marker.data.cp_collide === 'true',
  }));
  if (propDefs.length > 0) {
    loadProps(scene, propDefs, materialFactory);
    batchAllProps(scene);
  }

  // Initialize PVS
  const mapZones = getVisibilityZonesForMap(activeMap.id);
  initPVS(scene, mapZones);
  batchZoneGeometry(scene);

  // Initialize interactive elements (doors, ladders) for the active map —
  // prefer GLB-embedded markers over hardcoded per-map config.
  const interactiveManager = createInteractiveElementsForMap(
    scene.userData.activeMapId as string,
    scene,
    graphicalMap.data,
  );
  scene.userData.interactiveManager = interactiveManager;

  buildCollisionBVH(scene);

  // Wire collision BVH to the player collision system (capsule-vs-triangle)
  const bvhData = getCollisionBVH();
  if (bvhData) {
    setCollisionBVH(bvhData.bvh, bvhData.mesh);
  }

  // Rebuild BVH after blocking GLB props finish loading — their mesh geometry
  // provides more accurate collision than the procedural AABB fallbacks.
  onBlockingPropsLoaded(() => {
    buildCollisionBVH(scene);
    const updatedBVH = getCollisionBVH();
    if (updatedBVH) {
      setCollisionBVH(updatedBVH.bvh, updatedBVH.mesh);
    }
  });

  // F-MAP-010: lazy GLB swaps from props.ts must trigger a BVH rebuild so
  // collision tracks the new geometry instead of the procedural AABB fallback.
  onPropGLBSwapped(() => {
    buildCollisionBVH(scene);
    const swappedBVH = getCollisionBVH();
    if (swappedBVH) {
      setCollisionBVH(swappedBVH.bvh, swappedBVH.mesh);
    }
  });

  // Vegetation generation is intentionally disabled — de_outpost_v6 is a
  // military FOB, not a meadow, so the scattered grass blades looked out of
  // place on the map floor. The VegetationSystem module is kept wired so it
  // can be re-enabled by restoring the generateFromGraphicalData call.
  vegetation?.dispose();
  vegetation = null;
  scene.userData.vegetation = null;

  // Ensure all meshes have uv2 attribute (required for aoMap from ORM textures).
  // Copy uv → uv2 if uv2 is missing. This must run before material upgrade.
  scene.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    const geom = obj.geometry as THREE.BufferGeometry;
    if (geom.hasAttribute('uv') && !geom.hasAttribute('uv2')) {
      geom.setAttribute('uv2', geom.attributes.uv);
    }
  });

  // Async PBR texture upgrade: loads real KTX2 textures and swaps them in-place
  // on all cached materials. Runs in background — the scene renders immediately
  // with procedural fallback textures while PBR loads.
  pbrTextureLoader = new PBRTextureLoader(renderer);
  upgradeMaterials(pbrTextureLoader).catch((err) => {
    console.warn('PBR texture upgrade failed (procedural textures retained):', err);
  });

  // Phase 10: Initialize texture streamer for deferred texture loading
  textureStreamer = getTextureStreamer(4);

  // NOTE: resize is handled centrally in game.ts to keep renderer,
  // postprocessing, and camera framebuffers in sync.
  return { scene, renderer };
}

export function getScene(): THREE.Scene { return scene; }
export function getRenderer(): THREE.WebGLRenderer { return renderer; }
export function getRendererCapabilities(): RendererCapabilities | null { return rendererCapabilities; }
export function getSceneTextureStreamer(): TextureStreamer | null { return textureStreamer; }

/** Returns the active lightmap system (for quality updates from settings). */
export function getLightmapSystem(): LightmapSystem | null { return lightmapSystem; }

/** Update lightmap quality — called when the graphics preset changes. */
export function setLightmapQuality(quality: GraphicsQuality): void {
  lightmapSystem?.setQuality(quality);
}

// ═══════════════════════════════════════════════════════════════════════════
//  Cascaded Shadow Maps (CSM)
// ═══════════════════════════════════════════════════════════════════════════

/** Per-cascade shadow bias values (near cascade → far cascade). */
const CASCADE_BIAS: number[]        = [-0.0003, -0.0002, -0.0001, -0.00005];
const CASCADE_NORMAL_BIAS: number[] = [0.03, 0.02, 0.015, 0.01];

/**
 * Upgrade from a single DirectionalLight shadow to Cascaded Shadow Maps.
 * Call once after the PerspectiveCamera is created (from game.ts init).
 */
export function initCSM(camera: THREE.PerspectiveCamera): void {
  if (!scene || csm) return;

  const quality = settingsStore.get().graphicsQuality;

  // mobile-low: no shadows at all — keep scene as-is (no fallbackSun shadow either)
  if (quality === 'mobile-low') {
    renderer.shadowMap.enabled = false;
    return;
  }

  // mobile-medium: single shadow map 512x512, no CSM — keep fallbackSun
  if (quality === 'mobile-medium') {
    renderer.shadowMap.enabled = true;
    // fallbackSun already configured at 512x512 in addDefaultMapLighting
    return;
  }

  // Remove the fallback DirectionalLight — CSM creates its own cascade lights
  if (fallbackSun) {
    scene.remove(fallbackSun);
    fallbackSun.shadow.map?.dispose();
    fallbackSun.dispose();
    fallbackSun = null;
  }

  // mobile-high: CSM with 2 cascades, 1024x1024
  const isMobileHigh = quality === 'mobile-high';

  // de_outpost_v6 is dense (277 meshes, 421 colliders) — each cascade
  // resubmits the entire mesh tree, so shadow fillrate is the dominant
  // frame cost on mid-range desktop GPUs. 1024 shadow map (4× less
  // fillrate per cascade) with 4 cascades keeps banding invisible.
  const desktopShadowSize = 1024;

  csm = new CSM({
    camera,
    parent: scene,
    cascades: isMobileHigh ? 2 : 4,
    maxFar: isMobileHigh ? 100 : 150,
    shadowMapSize: isMobileHigh ? 1024 : desktopShadowSize,
    shadowBias: -0.0002,
    lightDirection: new THREE.Vector3(-0.5, -0.85, -0.35).normalize(),
    lightIntensity: 1.15,
    lightNear: 1,
    lightFar: 2000,
    lightMargin: 200,
    mode: 'practical',
  });

  // Enable fade between cascades (property — not settable via constructor)
  csm.fade = true;

  // Per-cascade bias tuning + warm desert sun color
  csm.lights.forEach((light: THREE.DirectionalLight, i: number) => {
    light.color.set(0xfff2d0);
    light.shadow.bias      = CASCADE_BIAS[i] ?? -0.0001;
    light.shadow.normalBias = CASCADE_NORMAL_BIAS[i] ?? 0.01;
  });

  // Re-compute frustum splits now that fade is enabled
  csm.updateFrustums();

  // Patch all existing scene materials so they receive CSM shadows
  scene.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((m) => {
        if (m && csm) csm.setupMaterial(m);
      });
    }
  });
}

/**
 * Must be called every frame before renderer.render().
 * L08: Intentionally a silent no-op when CSM is not initialized — this is
 * expected on mobile-low (no shadows) and mobile-medium (fallbackSun only).
 */
export function updateCSM(): void {
  csm?.update();
}

/** Returns the active CSM instance (for setupMaterial on dynamically added meshes). */
export function getCSM(): CSM | null {
  return csm;
}

/** Tear down CSM — called from disposeScene() and available for external cleanup. */
export function disposeCSM(): void {
  if (csm) {
    csm.remove();   // detach cascade lights from parent scene
    csm.dispose();   // clean up shader patches on materials

    // M13: Ensure CSM shader patches are fully reverted on all scene materials
    if (scene) {
      scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m) => {
            if (m instanceof THREE.Material && m.onBeforeCompile) {
              m.onBeforeCompile = () => {};
              m.needsUpdate = true;
            }
          });
        }
      });
    }

    csm = null;
  }
  if (fallbackSun) {
    fallbackSun.shadow.map?.dispose();
    fallbackSun.dispose();
    fallbackSun = null;
  }
}

export function disposeScene(): void {
  sceneDisposed = true;

  const interactiveManager = scene?.userData?.interactiveManager as
    | { dispose: () => void }
    | undefined;
  interactiveManager?.dispose();
  if (scene?.userData) {
    delete scene.userData.interactiveManager;
  }

  // M11: Dispose fallbackSun even if CSM was never initialized (prevents light leak)
  if (fallbackSun) {
    scene?.remove(fallbackSun);
    fallbackSun.shadow.map?.dispose();
    fallbackSun.dispose();
    fallbackSun = null;
  }

  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  const textures = new Set<THREE.Texture>();

  scene?.traverse((obj) => {
    // BUG-02: Dispose Light objects (PointLights, etc.) and their shadow maps
    if (obj instanceof THREE.Light) {
      if ((obj as any).shadow?.map) {
        (obj as any).shadow.map.dispose();
      }
      obj.dispose();
      return;
    }
    if (!(obj instanceof THREE.Mesh)) return;
    geometries.add(obj.geometry);
    const mat = obj.material;
    // L10: Validate individual entries are Material instances before adding to disposal set
    if (Array.isArray(mat)) {
      mat.forEach((entry) => {
        if (entry instanceof THREE.Material) materials.add(entry);
      });
    } else if (mat instanceof THREE.Material) {
      materials.add(mat);
    }
  });

  for (const material of materials) {
    const meshMat = material as THREE.MeshStandardMaterial;
    if (meshMat.map) textures.add(meshMat.map);
    if (meshMat.normalMap) textures.add(meshMat.normalMap);
    if (meshMat.roughnessMap) textures.add(meshMat.roughnessMap);
    if (meshMat.metalnessMap) textures.add(meshMat.metalnessMap);
    if (meshMat.aoMap) textures.add(meshMat.aoMap);
    if (meshMat.emissiveMap) textures.add(meshMat.emissiveMap);
    material.dispose();
  }
  textures.forEach((texture) => texture.dispose());
  geometries.forEach((geometry) => geometry.dispose());
  if (scene?.environment instanceof THREE.Texture) {
    scene.environment.dispose();
  }
  // F-RN-001: dispose PMREM render targets allocated by loadHDREnvMap /
  // createDesertEnvMap. The env texture above is owned by the RT, but the RT
  // itself (framebuffer + cubemap attachments + program) leaks on map switch
  // and replay reload without this call.
  disposeEnvMapTargets();
  textureCache.forEach(t => t.dispose());
  textureCache.clear();
  materialCache.forEach(m => { m.dispose(); });
  materialCache.clear();
  pbrTextureLoader?.dispose();
  pbrTextureLoader = null;
  lightmapSystem?.dispose();
  lightmapSystem = null;
  disposeTextureStreamer();
  textureStreamer = null;
  disposeCSM();
  disposeBlobShadows();
  disposeLoaderCache();
  disposeMapAssetState();
  disposeLightingZones();
  disposeTrimSheets();
  disposePermanentDecals(scene);
  disposeProps();
  disposeOcclusion();
  vegetation?.dispose();
  vegetation = null;
  renderer?.dispose();
}

/**
 * Per-frame vegetation animation update. The game loop is the only caller —
 * forwarding `dt` and the camera position lets grass shaders animate wind
 * and chunk LODs to track the player.
 */
export function updateVegetation(dt: number, cameraPosition: THREE.Vector3): void {
  vegetation?.update(dt, cameraPosition);
}

/** Returns the active vegetation system (null if not yet initialized). */
export function getVegetationSystem(): VegetationSystem | null {
  return vegetation;
}
